(ns minorodata.components.pipeline-editor.details.table
  (:require
   ["lucide-react" :refer [CheckCheck X]]
   ["react" :as react]
   [clojure.set :as c.set]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :as app.utils :refer [format-date]]
   [ui.data-grid.preview :as data-grid-preview]
   [minorodata.components.pipeline-editor.details.utils :refer [selected-node-bar
                                                                tab-actions]]
   [minorodata.components.pipeline-editor.utils :refer [data-type-icon]]
   [minorodata.components.spinner.core :as spinner :refer [with-text]]
   [minorodata.components.tables.details :refer [use-query]]
   [minorodata.sandbox.events :refer [reset-preview]]
   [re-posh.core :as re-posh]
   [ui.button :as button]
   [ui.table :refer [sort-label table tbody td th thead tr]]
   [ui.tabs :as tabs]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn query-unit-tests [{:keys [catalog schema table snowflake?]}]
  (str "SELECT _TEST_TIMESTAMP as timestamp,
        _TEST_COLUMN as \"column\",
        _TEST_TYPE as type,
        _TEST_RESULT as result,
        _TEST_DESCRIPTION as description
 FROM "
       (when snowflake? "PROD.")
       "KLEENE.UNIT_TEST_LOG
        WHERE _TEST_DATABASE = '" catalog "'
          AND _TEST_SCHEMA = '" schema "'
          AND _TEST_TABLE = '" table "'
        ORDER BY _TEST_TIMESTAMP DESC;"))

(defn selected-table-bar [{:keys [selected loading data]}]
  (let [sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [selected-node-bar {:selected selected
                        :loading  loading
                        :name     (or (:id data) "Table doesn't exist")
                        :description (when-not sandbox?
                                       (when data
                                         (str (:row-count data) " rows • Updated at " (some-> (:created data) format-date))))
                        :sandbox? sandbox?}]))

(defn empty-row [headers]
  [(mapv (constantly nil) headers)])

(defn table-preview' [{:keys [loading dark-mode? table-preview]
                       {:keys [headers rows]} :table-preview}]
  (cond
    loading
    [:div {:class "flex gap-2 items-center px-10 py-6"}
     [spinner/spinner-lucide] "Loading data"]
    (:error table-preview)
    [:div {:class "flex gap-2 items-center px-10 py-6"}
     "Table does not exist"]
    :else
    [:div {:class "h-full overflow-hidden"}
     [:f> data-grid-preview/table
      {:headers                   headers
       :rows                      (if (not-empty rows)
                                    rows (empty-row headers))
       :dark-mode?                dark-mode?
       :height                    "100%"
       :column-width              150
       :sortable?                 false
       :filterable?               true
       :resizable?                true
       :row-numbers?              true
       :suppress-no-rows-overlay? true}]]))

(defn table-preview [{:keys [table-id] :as params}]
  (let [table-preview @(re-posh/subscribe [:pipeline-editor.subs/table-preview table-id])
        loading       (or @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-live-table])
                          @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-table])
                          @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-live-table-preview])
                          @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-preview]))
        dark-mode?    @(re-posh/subscribe [:app/prop :app/dark-mode?])]
    [:f> table-preview' (assoc params
                               :table-preview table-preview
                               :dark-mode?    dark-mode?
                               :loading      loading)]))

(defn type-icon [{:keys [type]}]
  (let [icon (data-type-icon type)]
    [icon]))

(defn table-columns' [{:keys [columns]}]
  (let [[sort-option set-sort-option] (react/useState nil)
        columns (mapv #(c.set/rename-keys % {:column-name :column :type-name :type}) columns)
        columns (cond->> columns
                  sort-option (sort-by :column (if (= sort-option "asc") > <)))]
    [:div
     [table {:style {"--background" "transparent"}}
      [thead {:class "h-14"}
       [tr
        [th {:class "w-80 border-r"}
         [sort-label {:active    sort-option
                      :direction sort-option
                      :on-click  (fn []
                                   (set-sort-option #(case %
                                                       nil    "desc"
                                                       "desc" "asc"
                                                       "asc"  nil)))}
          "Column"]]
        [th {:class "w-40"} "Type"]
        #_[th "Example value"]]]
      [tbody
       (for [{:keys [column type _example-value]} columns]
         ^{:key column}
         [tr {:class "h-auto"}
          [td {:class "border-r"}
           column]
          [td {:class "flex gap-2 items-center"}
           [type-icon {:type type}]
           type]
          #_[td example-value]])]]]))


(defn table-columns [{:keys [columns loading]}]
  (cond
    loading          [:div {:class "flex gap-2 h-full items-center justify-center"}
                      [spinner/spinner-lucide] "Loading columns data"]
    (empty? columns) [:div {:class "px-10 py-6"} "No data"]
    :else
    [:f> table-columns' {:columns columns}]))


(defn table-unit-test-results' [{:keys [loading] :as props}]
  (let [unit-tests (use-query (query-unit-tests props))]
    (cond
      loading
      [with-text]

      (empty? unit-tests)
      [:div {:class "flex gap-2 h-full items-center justify-center"}
       [typography/p {:class "text-kleene-text-secondary"} "No unit tests available for this table"]]

      :else
      [:<>
       [table {:data-cy       "logs-table"
               :sticky-header true}
        [thead {:class "h-14"}
         [tr {:sticky true}
          [th {:style {:width "180px"} :class "border-r"}
           "Date"]
          [th {:style {:width "80px"} :class "border-r text-center"}
           "Result"]
          [th {:style {:width "200px"} :class "border-r"}
           "Type"]
          [th {:class "border-r"}
           "Column"]
          [th "Description"]]]
        [tbody {}
         (for [[timestamp column type result description] (:rows unit-tests)]
           ^{:key (str timestamp "-" type)}
           [tr {:class "h-auto"}
            [td {:class "border-r"}
             (format-date timestamp)]
            [td {:class "[&>svg]:mx-auto border-r"}
             (if result
               [:> CheckCheck {:class "text-kleene-text-success"}]
               [:> X {:class "text-kleene-error-01"}])]
            [td {:class "flex gap-2 items-center border-r"}
             [type-icon {:type type}]
             type]
            [td {:class "border-r"} column]
            [td description]])]]])))

(defn table-unit-test-results [props]
  (let [warehouse-type @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        loading        @(re-posh/subscribe [:mold-http/loading? :sql-console.api/post-query])]
    [:f> table-unit-test-results' (assoc props
                                         :snowflake? (= "snowflake" warehouse-type)
                                         :loading    loading)]))


(defn table-details-tabs' [{:keys [id data loading disabled]}]
  (let [unit-tests        (:unit-tests data)
        unit-tests-total  (count unit-tests)
        unit-tests-passed (count (filter #(:result %) unit-tests))
        [tab set-tab] (react/useState "preview-data")]

    [tabs/tabs-root {:value           tab
                     :on-value-change set-tab
                     :class           "grid grid-rows-[auto_1fr] overflow-hidden"}
     [tabs/tabs-list {:variant :tab :class "px-10"}
      [tabs/tabs-trigger {:value "preview-data" :variant :tab :disabled disabled} "Preview data"]
      [tabs/tabs-trigger {:value "columns" :variant :tab :disabled disabled} "Columns"]
      [tabs/tabs-trigger {:value "unit-test-results" :variant :tab :disabled (or disabled (empty? (:unit-tests data)))}
       [:div
        "Unit test results"
        (when (pos? unit-tests-total)
          [:span {:class (clsx "text-xs ml-2 w-4 h-4 px-1 py-0.5"
                               (cond
                                 (zero? unit-tests-total) "bg-gray-100 text-gray-500 dark:bg-gray-900"
                                 (zero? unit-tests-passed) "bg-red-100 text-red-500 dark:bg-red-900"
                                 (= unit-tests-passed unit-tests-total) "bg-green-100 text-green-500 dark:bg-green-900"
                                 :else "bg-yellow-100 text-yellow-500 dark:bg-yellow-900"))}
           (str unit-tests-passed "/" unit-tests-total)])]]

      [tab-actions {:tab tab
                    :id   id}]]

     [tabs/tabs-content {:value "preview-data" :class "overflow-hidden"}
      [table-preview {:table-id id}]]
     [tabs/tabs-content {:value "columns" :class "overflow-y-auto"}
      [table-columns {:columns (:columns data)
                      :loading loading}]]
     [tabs/tabs-content {:value "unit-test-results" :class "overflow-y-auto"}
      [table-unit-test-results data]]]))

(defn table-details-tabs [props]
  [:f> table-details-tabs' props])

(defn table-details [{:keys [selected]}]
  (let [data (some->
              @(re-posh/subscribe [:pipeline-editor.subs/table (:id selected)])
              app.utils/drop-ns-in-keys)
        loading (or @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-live-table])
                    @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-table]))
        not-existing? (and (not loading) (not data))]

    (react/useEffect
     (fn []
       (dispatch [:pipeline-editor.events/fetch-full-table {:params {:id (:id selected)}}])

       js/undefined)
     #js [(:id selected)])

    [:div {:class "grid h-full grid-rows-[auto_1fr] overflow-hidden"}
     [selected-table-bar {:selected selected
                          :data data
                          :loading loading}]
     (cond
       (not-empty data)
       [table-details-tabs {:id      (:id selected)
                            :data    data
                            :loading loading
                            :disabled not-existing?}]

       loading
       [:div {:class "flex items-center justify-center h-full"}
        [spinner/with-text {:text "Loading table..."}]]

       :else
       [:div {:class "flex flex-col gap-2 items-center justify-center h-full"}
        [typography/h2 "Table not found"]
        (when (:preview selected)
          [button/button {:variant :secondary
                          :on-click #(dispatch [:app.events/navigate-query reset-preview])}
           "Close preview"])])]))
